(async () => {
    const { Core } = await importModule('Core');

    const CN_ACTIVE = '!active';

    // How far above whatever's found in the corner to sit, and a cap on how
    // far up the button will ever move - a chat widget's window can grow to
    // nearly full-height once someone opens it, and chasing that would fling
    // the button somewhere useless rather than just sitting a sensible
    // distance above its closed bubble. (Sized to clear a bubble plus the
    // Shopify theme-preview bar that can sit under it, since the widget is
    // at most MAX_WIDGET_DIMENSION tall.)
    const CORNER_GAP = -6;
    const MAX_CLEARANCE = 160;
    const PROBE_INTERVAL = 500;
    const MAX_PROBES = 60; // ~30s of polling before giving up
    // A genuine corner widget (chat bubble, etc.) is compact. Wider/taller
    // fixed elements that merely happen to intersect the sampled corner
    // point - this theme's own full-viewport <dialog> modals (toast/cart
    // notifications, popups), or a full-width bar from a third-party
    // script - are excluded so they're never mistaken for it (see
    // _findCornerWidget below).
    const MAX_WIDGET_DIMENSION = 220;

    customElements.define('scroll-to-top-button', class extends Core {
        elements = {
            trigger: '[data-trigger]',
            button: '[data-button]'
        }

        render() {
            this._handleTrigger();
            this._handleClick();
            this._avoidCornerWidget();
        }

        _handleTrigger() {
            const observer = new IntersectionObserver((entries) => {
                this.active = !entries[0].isIntersecting;
            });
            observer.observe(this.$trigger);
        }

        _handleClick() {
            this.addEventListener('click', this._onClick.bind(this));
        }

        _onClick() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Third-party widgets that sit fixed in the bottom-right corner
        // (tawk.to's chat bubble being the one on this site) load their own
        // markup asynchronously and don't share a stable, documented
        // selector to watch for - their bubble can be plain divs/images
        // rather than an iframe until someone actually opens the chat
        // window, so guessing at their DOM structure is fragile. Instead
        // this periodically samples what's actually sitting in that corner
        // of the viewport (ignoring this button's own element) and, if it
        // finds something else fixed/sticky there, matches this button's
        // size to it and lines the two up on the same horizontal center,
        // stacked with a small gap rather than each keeping its own fixed
        // size/offset and hoping they happen to line up.
        _avoidCornerWidget() {
            let probes = 0;

            const probe = () => {
                probes++;
                const widget = this._findCornerWidget();
                if (widget) {
                    this._trackCornerWidget(widget);
                    return;
                }
                if (probes < MAX_PROBES) {
                    setTimeout(probe, PROBE_INTERVAL);
                }
            };

            probe();
        }

        // Shopify Inbox is the exception to the "sample the corner" approach
        // below, so it's looked for first. It draws its chat button inside a
        // shadow root, under a <shopify-chat> host that is fixed over the
        // whole viewport (and ignores pointer events). Sampling the corner
        // therefore never reaches the button - the host is far too big to
        // pass as a corner widget, and elementsFromPoint can't see into its
        // shadow root - and the pill also sits a bit further in from the
        // edge than the sampled point. Its markup is stable enough to target
        // directly: the visible button is the .activator element.
        _findShopifyInboxButton() {
            const host = document.querySelector('shopify-chat');
            const root = host && host.shadowRoot;
            if (!root) return null;
            const $activator = root.querySelector('.activator');
            if (!$activator) return null;
            const rect = $activator.getBoundingClientRect();
            return rect.width && rect.height ? $activator : null;
        }

        _findCornerWidget() {
            const $inbox = this._findShopifyInboxButton();
            if ($inbox) return $inbox;

            // A couple of centimetres in from the very corner - inside
            // where a chat bubble normally sits, whatever its exact margin.
            const x = window.innerWidth - 28;
            const y = window.innerHeight - 28;
            const stack = document.elementsFromPoint(x, y);

            const candidates = stack.filter(($el) => {
                if ($el === this || this.contains($el)) return false;
                const style = getComputedStyle($el);
                if (style.position !== 'fixed' && style.position !== 'sticky') return false;
                const rect = $el.getBoundingClientRect();
                if (!rect.width || !rect.height) return false;
                // Must actually occupy the bottom-right corner itself, not
                // just happen to be the element sampled at that point (e.g.
                // a full-page overlay).
                if (rect.right <= window.innerWidth - 220 || rect.bottom <= window.innerHeight - 220) return false;
                // Must actually be corner-widget-sized. Without this, a
                // full-viewport modal backdrop or a full-width fixed bar
                // still passes the corner check above (its edges do reach
                // the corner - it just also covers the rest of the page)
                // and gets mistaken for a compact bubble, which is what
                // sends this button drifting toward the horizontal middle
                // of the screen instead of sitting in the corner.
                if (rect.width > MAX_WIDGET_DIMENSION || rect.height > MAX_WIDGET_DIMENSION) return false;
                return true;
            });

            if (!candidates.length) return null;

            // A chat widget's actual round bubble is usually nested inside
            // a larger positioning wrapper (which can cover a much bigger
            // area than what's visibly drawn, e.g. room for an expanded
            // chat panel), and both can land in the same point sample.
            // Picking the smallest match rather than just the first favors
            // the actual visible button over its wrapper.
            return candidates.reduce((smallest, $el) => {
                const rect = $el.getBoundingClientRect();
                const smallestRect = smallest.getBoundingClientRect();
                return rect.width * rect.height < smallestRect.width * smallestRect.height ? $el : smallest;
            });
        }

        _trackCornerWidget($widget) {
            const update = () => {
                const rect = $widget.getBoundingClientRect();
                if (!rect.height || !rect.width) {
                    // The tracked widget has closed, been removed, or is
                    // simply not rendered right now (e.g. a chat panel
                    // that collapses to nothing between states) - fall
                    // back to this button's own default corner spot
                    // instead of leaving it stuck wherever it was last
                    // positioned.
                    this.$button.style.removeProperty('--corner-size');
                    this.$button.style.removeProperty('--corner-clearance');
                    this.$button.style.removeProperty('--corner-right');
                    return;
                }

                // Match this button's own footprint to the widget's rather
                // than keeping its own fixed size, so the two read as a
                // matched pair - but pulled in a bit smaller than the
                // widget itself so it reads as the secondary control of the
                // pair. Clamped to a sane, compact bubble-icon range in
                // case what got matched is still a wrapper rather than the
                // visible button itself.
                const size = Math.min(Math.max(Math.max(rect.width, rect.height) * 0.82, 34), 50);
                this.$button.style.setProperty('--corner-size', `${size}px`);

                const raised = Math.max(0, window.innerHeight - rect.top);
                const clearance = Math.min(Math.max(raised + CORNER_GAP, 0), MAX_CLEARANCE);
                this.$button.style.setProperty('--corner-clearance', `${clearance}px`);

                // Center this button on the same horizontal point as the
                // widget instead of each independently hugging the right
                // edge with its own margin. A fixed element's `right` is
                // measured from the edge of the area inside any vertical
                // scrollbar (clientWidth), not from window.innerWidth, so
                // use that or the two end up a scrollbar-width apart on
                // desktop.
                const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
                const centerX = rect.left + rect.width / 2;
                const right = Math.max(viewportWidth - centerX - size / 2, 0);
                this.$button.style.setProperty('--corner-right', `${right}px`);
            };

            update();
            window.addEventListener('resize', update);
            if (window.ResizeObserver) {
                new ResizeObserver(update).observe($widget);
            }

            // A widget inside a shadow root (Shopify Inbox) can move or
            // appear/disappear - e.g. its intro nudge closing, or the chat
            // window opening and hiding the button - without its own size
            // changing, so also re-measure whenever that shadow root's
            // contents or attributes change.
            const root = $widget.getRootNode();
            if (window.ShadowRoot && root instanceof ShadowRoot) {
                new MutationObserver(update).observe(root, {
                    attributes: true,
                    childList: true,
                    subtree: true
                });
            }
        }

        set active(state) {
            this.$button.classList.toggle(CN_ACTIVE, state);
        }
    });
})();
